import { JSX, useCallback } from 'react';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import type { InterviewState } from 'src/redux/slices/Interview/types';
import { useInterviewOverlay } from './InterviewOverlayContext';

export default function InterviewHeader(): JSX.Element {
  const navigate = useNavigate();
  const { certCategory, effectiveDate, propertyName, loading, backPath } =
    useSelector((state: { interview: InterviewState }) => state.interview);

  const isLoading = loading && !certCategory;

  const { getBackOverride } = useInterviewOverlay();

  const resolvedBackPath = backPath ?? `/${RENTER_PATHS.HOME}`;

  const handleBack = useCallback(() => {
    const override = getBackOverride();
    if (override) {
      override();
    } else {
      navigate(resolvedBackPath);
    }
  }, [getBackOverride, navigate, resolvedBackPath]);

  const formattedDate = effectiveDate
    ? new Date(effectiveDate + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const headline =
    certCategory && formattedDate
      ? `${certCategory} - ${formattedDate}`
      : certCategory || formattedDate || '';

  return (
    <Box
      sx={{
        height: { xs: 80, md: 72 },
        bgcolor: 'primary.dark',
        display: 'flex',
        alignItems: 'center',
        px: { xs: 2.5, md: 5 },
        gap: 1.75,
      }}
    >
      <IconButton
        onClick={handleBack}
        aria-label="Go back"
        size="small"
        sx={{
          backgroundColor: 'common.white',
          color: 'grey.900',
          width: 34,
          height: 34,
          borderRadius: '50%',
          boxShadow: '0px 1px 4px rgba(145,158,171,0.06)',
          flexShrink: 0,
          '&:hover': { backgroundColor: 'grey.100' },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Box>
        {isLoading ? (
          <>
            <Skeleton
              variant="text"
              width={120}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                fontSize: { xs: 15, md: 16 },
              }}
            />
            <Skeleton
              variant="text"
              width={80}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                fontSize: { xs: 12, md: 13 },
              }}
            />
          </>
        ) : (
          <>
            <Typography
              sx={{
                color: 'primary.contrastText',
                fontSize: { xs: 15, md: 16 },
                fontWeight: 600,
                letterSpacing: -0.2,
              }}
            >
              {headline || 'Home'}
            </Typography>
            {propertyName && (
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: { xs: 12, md: 13 },
                }}
              >
                {propertyName}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
